import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Modal,
  Button,
  Logo,
  TemplateInput,
  TemplateTextarea,
  Tab,
  TabGroup,
  FormError,
} from "@components";
import { SmsEditor } from "@components/paymentTemplates";
import { ChatLeftDots, Envelope } from "react-bootstrap-icons";
import { useMerchant } from "@auth";
import { templateTokens, formatAmount } from "@utils";
import { toast } from "@components/toast/Toast";
import { createpaymentRequestReminderAsync } from "@api";
import { DEFAULT_REMINDER_SUBJECT, buildDefaultReminderMessage } from "./reminderDefaults";
import { buildPaymentUrl } from "./reminder";

// Reminder bodies are plain text inserted into a branded template, so suppress the token affordance
// (there are no tokens to resolve) — same approach as the smart-note document-email editor.
const NO_TOKENS = templateTokens.map((t) => t.value);

// An edit-in-place reminder editor: the branded email (editable subject + body) or an SMS message,
// sent straight from here. Replaces the read-only preview + separate send form on the tasks page.
export default function EditReminderModal({ isOpen, paymentRequest, onClose, onSent }) {
  const { merchant } = useMerchant();
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState("Sms");

  const {
    control,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: { subject: "", emailBody: "", smsText: "", email: "", phone: "" },
  });

  useEffect(() => {
    if (!isOpen || !paymentRequest) return;
    const body = buildDefaultReminderMessage(paymentRequest);
    reset({
      subject: DEFAULT_REMINDER_SUBJECT,
      emailBody: body,
      smsText: body,
      email: paymentRequest.email?.to || "",
      phone: paymentRequest.sms?.to || "",
    });
    setChannel(paymentRequest.communicationMethod === "Email" ? "Email" : "Sms");
  }, [isOpen, paymentRequest, reset]);

  if (!paymentRequest) return null;

  const isEmail = channel === "Email";
  const primaryColour = merchant?.customisationSettings?.colourSet?.primary;
  const buttonTextColour = merchant?.customisationSettings?.colourSet?.buttonText;
  const merchantName = merchant?.name ?? "Your company";
  const amount = formatAmount(paymentRequest.amountRequested);

  const channels = [
    { name: "Send SMS", value: "Sms", icon: ChatLeftDots },
    { name: "Send Email", value: "Email", icon: Envelope },
  ];

  const send = async () => {
    const valid = await trigger(isEmail ? ["email", "subject", "emailBody"] : ["phone", "smsText"]);
    if (!valid) return;

    const v = getValues();
    setSending(true);
    try {
      await createpaymentRequestReminderAsync(
        paymentRequest.id,
        buildPaymentUrl(paymentRequest.id),
        channel,
        isEmail ? null : v.phone,
        isEmail ? v.email : null,
        isEmail ? v.subject : null,
        isEmail ? null : v.smsText,
        isEmail ? v.emailBody : null,
        true, // sent from the Today's-tasks (suggestion) flow
      );
    } catch {
      setSending(false);
      toast({ description: "Couldn't send the reminder. Please try again." });
      return;
    }

    setSending(false);
    toast({ description: `Reminder sent to ${paymentRequest.customerName || "the customer"}.` });
    onSent?.();
    onClose?.();
  };

  return (
    <Modal
      title="Send reminder"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      fullHeight
      maxHeight="780px"
      elevated
      bodyClassName="flex min-h-0 flex-col overflow-hidden bg-white"
    >
      <div className="shrink-0 border-b border-zinc-200 px-4 pt-3">
        <TabGroup>
          {channels.map((c, i) => (
            <Tab
              key={i}
              id={`reminder-channel-${i}`}
              title={c.name}
              value={c.value}
              active={channel === c.value}
              onClick={() => setChannel(c.value)}
            >
              <c.icon className="mr-2" />
              {c.name}
            </Tab>
          ))}
        </TabGroup>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isEmail ? (
          <div className="flex w-full flex-col items-center px-6 pb-8 pt-6">
            {/* Edit-in-place branded email — subject + body only (plain text, inserted into the
                branded template downstream). */}
            <div
              className="flex w-full flex-col items-stretch rounded-3xl bg-white pt-6"
              style={{ boxShadow: "4px 4px 16px 0px rgba(0,0,0,0.16)" }}
            >
              <div className="border-b border-zinc-200 px-6 pb-4">
                <div className="space-y-1 px-2 text-xs">
                  <div className="flex items-center px-2 py-1">
                    <span className="w-16 shrink-0 text-zinc-400">From</span>
                    <span className="text-zinc-400">{merchantName}</span>
                  </div>
                  <div className="flex items-center px-2 py-1">
                    <span className="w-16 shrink-0 text-zinc-400">To</span>
                    <Controller
                      name="email"
                      control={control}
                      rules={{ required: "A recipient email is required" }}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="email"
                          placeholder="customer@email.com"
                          className="min-w-0 flex-1 border-none bg-transparent p-0 text-xs text-zinc-800 outline-none placeholder:text-zinc-400"
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center px-2 py-1">
                    <span className="w-16 shrink-0 text-zinc-400">Subject</span>
                    <div className="min-w-0 flex-1 font-semibold text-zinc-800">
                      <TemplateInput
                        name="subject"
                        control={control}
                        displayName="Subject"
                        required
                        borderless
                        maxLength={150}
                        className="text-xs"
                        blacklistedTokens={NO_TOKENS}
                      />
                    </div>
                  </div>
                </div>
                <FormError name="email" errors={errors} />
                <FormError name="subject" errors={errors} />
              </div>

              <div className="px-8">
                <div className="flex justify-center p-3 pt-7">
                  <Logo type="center" brand={merchant?.organisationId} height="96px" width="200px" />
                </div>
                <div className="mt-4 text-center text-4xl font-bold">Payment Required</div>
                <div className="mt-5 text-center text-xs text-zinc-500">
                  {paymentRequest.customerName || "Customer"}
                  {paymentRequest.reference ? ` (${paymentRequest.reference})` : ""}
                </div>

                <div className="my-8">
                  <TemplateTextarea
                    name="emailBody"
                    control={control}
                    displayName="Email body"
                    required
                    borderless
                    maxLength={2000}
                    className="text-sm"
                    blacklistedTokens={NO_TOKENS}
                  />
                  <FormError name="emailBody" errors={errors} />
                </div>

                <div className="flex flex-col rounded-3xl bg-zinc-100 p-6">
                  <p className="pb-4 text-md text-zinc-900">Amount due</p>
                  <p className="w-full truncate pb-6 text-3xl font-semibold text-zinc-900">{amount}</p>
                  <Button
                    className="w-full rounded-xl py-2 font-medium"
                    customColor={{ backgroundColor: primaryColour, textColor: buttonTextColour }}
                  >
                    Review and pay
                  </Button>
                </div>

                <div className="my-6 text-center text-xs font-medium text-zinc-700">
                  Powered by <strong>Ontik</strong>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col pb-8">
            {/* Recipient as a compose-style "To" header — mirrors the email tab and messaging apps. */}
            <div className="mx-auto w-full max-w-xl px-6 pt-6">
              <div className="flex items-center gap-3 rounded-xl bg-zinc-50 px-4 py-2.5 text-sm">
                <span className="shrink-0 text-zinc-400">To</span>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: "A recipient mobile number is required" }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="tel"
                      placeholder="Customer mobile"
                      className="min-w-0 flex-1 border-none bg-transparent p-0 text-sm text-zinc-800 outline-none placeholder:text-zinc-400"
                    />
                  )}
                />
              </div>
              <FormError name="phone" errors={errors} />
            </div>

            {/* The same editable SMS bubble as the payment-request template flow. */}
            <SmsEditor control={control} errors={errors} />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-3 border-t border-zinc-200 bg-white px-5 py-3">
        <Button color="secondary" onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button color="primary" onClick={send} disabled={sending}>
          {sending ? "Sending…" : "Send reminder"}
        </Button>
      </div>
    </Modal>
  );
}
